import React from 'react';

/**
 * EvaluationModal — full-screen overlay that surfaces the LLM
 * evaluation of a canvas section.
 *
 * Two modes:
 *   SAVE mode (default): runs after a deep-dive completes. evalState
 *     carries the stage ('evaluating' | 'done' | 'error') and the
 *     pending payload. Footer offers Save & continue + Refine
 *     (return to chat, don't save) + optional Save & open Quick
 *     Select.
 *
 *   READ-ONLY mode (readOnly=true): pops a saved evaluation back up
 *     so the leader can re-read the score, strengths, and
 *     improvements any time. Pass evaluation via
 *     evalState.evaluation with stage='done'. Footer offers
 *     Close + Refine + optional Re-evaluate.
 *
 * section: { title, icon, colour } — drives the header chrome.
 * onContinue: save the payload and dismiss (SAVE mode).
 * onCancel: dismiss without saving (both modes).
 * onRefine: jump back into the dialogue without saving (both modes).
 * onOpenQuickSelect: save and jump to Quick Select editor (SAVE mode).
 * onReEvaluate: re-rate the current saved fields (READ-ONLY mode).
 * refineLabel: button text for Refine — defaults to a chat-flavoured
 *   label; OM dialogues may want "Refine — back to dialogue".
 */
export default function EvaluationModal({
  section,
  evalState,
  onContinue,
  onCancel,
  onRefine,
  onOpenQuickSelect,
  onReEvaluate,
  readOnly = false,
  refineLabel = 'Refine — back to conversation',
}) {
  const e = evalState?.evaluation;
  const stage = evalState?.stage;
  return (
    <div role="dialog" aria-modal="true" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }}>
      <div style={{
        background: 'white', borderRadius: 14,
        maxWidth: 520, width: '100%', maxHeight: '90vh',
        boxShadow: '0 18px 50px rgba(0,0,0,0.25)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '1.1rem 1.4rem 0.85rem', borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: '1.4rem', color: section.colour }}>{section.icon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', color: '#aaa', textTransform: 'uppercase' }}>
              {readOnly ? 'Saved review' : 'End of section'}
            </div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a1a2e' }}>{section.title} review</div>
            {readOnly && e?.evaluatedAt && (
              <div style={{ fontSize: '0.72rem', color: '#aaa', marginTop: 2 }}>
                Reviewed {new Date(e.evaluatedAt).toLocaleString()}
              </div>
            )}
          </div>
          {readOnly && (
            <button onClick={onCancel} aria-label="Close"
              style={{
                border: 'none', background: 'none', color: '#888',
                fontSize: '1.1rem', cursor: 'pointer', width: 'auto', padding: '0 4px',
                lineHeight: 1, fontFamily: 'inherit',
              }}>{'✕'}</button>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '1.1rem 1.4rem', overflowY: 'auto', flex: 1 }}>
          {stage === 'evaluating' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '1rem 0' }}>
              <div style={{
                width: 38, height: 38, borderRadius: '50%',
                border: '3px solid #e5e7eb', borderTopColor: section.colour,
                animation: 'evalModalSpin 1s linear infinite',
              }} />
              <div style={{ fontSize: '0.92rem', color: '#444', fontWeight: 500 }}>
                Reviewing your {section.title.toLowerCase()}{'…'}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#888', textAlign: 'center', maxWidth: 360, lineHeight: 1.5 }}>
                I'm reading back what you captured to score it on depth, consistency, and clarity. This usually takes about 10 seconds.
              </div>
              <style>{'@keyframes evalModalSpin { to { transform: rotate(360deg); } }'}</style>
            </div>
          )}

          {stage === 'done' && e && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '0.8rem' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: section.colour, color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.6rem', fontWeight: 800, flexShrink: 0,
                }}>
                  {e.score}/5
                </div>
                <div style={{ fontSize: '0.88rem', color: '#1a1a2e', lineHeight: 1.55, fontWeight: 500 }}>
                  {e.headline || 'Section reviewed.'}
                </div>
              </div>
              {e.strengths && e.strengths.length > 0 && (
                <div style={{ marginBottom: '0.85rem' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#0F6E56', marginBottom: 5 }}>What's working</div>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.83rem', color: '#1a1a2e', lineHeight: 1.55 }}>
                    {e.strengths.map((s, i) => <li key={i} style={{ marginBottom: 3 }}>{s}</li>)}
                  </ul>
                </div>
              )}
              {e.improvements && e.improvements.length > 0 && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '0.75rem 0.9rem', marginBottom: '0.5rem' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#92400e', marginBottom: 5 }}>How to improve</div>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.83rem', color: '#7B4A0F', lineHeight: 1.55 }}>
                    {e.improvements.map((s, i) => <li key={i} style={{ marginBottom: 3 }}>{s}</li>)}
                  </ul>
                </div>
              )}
              {/* Off-card-gap note — the evaluator may flag scope-bounded
                  assumptions ("4/5 assuming pricing rationale is captured
                  in Revenue Model"). Rendered separately so leaders can
                  see what the score was conditional on. */}
              {e.assumptions && e.assumptions.length > 0 && (
                <div style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.65rem 0.85rem', marginTop: '0.4rem' }}>
                  <div style={{ fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280', marginBottom: 4 }}>
                    Score assumes
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.78rem', color: '#374151', lineHeight: 1.5 }}>
                    {e.assumptions.map((s, i) => <li key={i} style={{ marginBottom: 2 }}>{s}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}

          {stage === 'error' && (
            <div style={{ fontSize: '0.88rem', color: '#7f1d1d', lineHeight: 1.6, padding: '0.5rem 0' }}>
              I couldn't generate a review just now {'—'} but your answers are saved and the stars will reflect what you've captured. You can re-open this section to refine it any time.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '0.85rem 1.4rem', borderTop: '1px solid #f0f0f0',
          display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap',
        }}>
          {stage === 'evaluating' && (
            <button onClick={onCancel}
              style={{
                background: 'white', color: '#666', border: '1px solid #ddd',
                borderRadius: 8, padding: '0.55rem 1.1rem', fontSize: '0.85rem',
                fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
              }}>
              Cancel
            </button>
          )}
          {stage !== 'evaluating' && !readOnly && (
            <>
              {onRefine && (
                <button onClick={onRefine}
                  style={{
                    background: 'white', color: '#185FA5', border: '1.5px solid #185FA5',
                    borderRadius: 8, padding: '0.55rem 1.1rem', fontSize: '0.85rem',
                    fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                  }}>
                  {refineLabel}
                </button>
              )}
              {onOpenQuickSelect && (
                <button onClick={onOpenQuickSelect}
                  style={{
                    background: 'white', color: '#9B6B3A', border: '1.5px solid #9B6B3A',
                    borderRadius: 8, padding: '0.55rem 1.1rem', fontSize: '0.85rem',
                    fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                  }}>
                  Save &amp; open Quick Select
                </button>
              )}
              <button onClick={onContinue}
                style={{
                  background: '#1D9E75', color: 'white', border: 'none',
                  borderRadius: 8, padding: '0.6rem 1.25rem', fontSize: '0.88rem',
                  fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                }}>
                Save &amp; continue
              </button>
            </>
          )}
          {stage !== 'evaluating' && readOnly && (
            <>
              {onReEvaluate && (
                <button onClick={onReEvaluate}
                  title="Re-rate this section against the current saved fields, without redoing the conversation"
                  style={{
                    background: 'white', color: '#666', border: '1px solid #ccc',
                    borderRadius: 8, padding: '0.55rem 1.1rem', fontSize: '0.85rem',
                    fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                  }}>
                  Re-evaluate
                </button>
              )}
              {onRefine && (
                <button onClick={onRefine}
                  style={{
                    background: 'white', color: '#185FA5', border: '1.5px solid #185FA5',
                    borderRadius: 8, padding: '0.55rem 1.1rem', fontSize: '0.85rem',
                    fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                  }}>
                  {refineLabel}
                </button>
              )}
              <button onClick={onCancel}
                style={{
                  background: '#1a1a2e', color: 'white', border: 'none',
                  borderRadius: 8, padding: '0.55rem 1.1rem', fontSize: '0.85rem',
                  fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: 'auto',
                }}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
